import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { FetchPolicy } from '@apollo/client/core';
import { AppConfig } from '@app/core/app.config';
import { Project, ProjectInput } from '@app/core/model/entities/project/project';
import { ActivationEndService } from '@app/features/main/activation-end.service';
import { SelectParams } from '@app/shared/grid/datasource-utils';
import ApiService from '@services/api.service';
import { AppManager } from '@services/managers/app.manager';
import { gql } from 'apollo-angular';
import { plainToInstance } from 'class-transformer';
import { EMPTY, expand, from, Observable, Subject, switchMap } from 'rxjs';
import { map, reduce, tap, toArray } from 'rxjs/operators';

@Injectable({providedIn: 'root'})
export class ProjectsService {
  private apiService = inject(ApiService);
  private appManager = inject(AppManager);
  private router = inject(Router);
  private activationEndService = inject(ActivationEndService);
  private appConfig = inject(AppConfig);

  private sidePanelToggleSubject = new Subject<Project | null>();

  private addProjectSubject = new Subject<Project>();
  private updateProjectSubject = new Subject<Project>();
  private deleteProjectsSubject = new Subject<Project[]>();

  private readonly projectInfoGraphqlFragment = gql`
    fragment ProjectInfo on Project {
      id
      identifier
      name
      assetIdsList
      projectIdsList
      properties
      computedProperties
      organizationId
      creationActor {
        actorId
        name
      }
      creationDate
      lastChangeActor {
        actorId
        name
      }
      lastChangeDate
      dataDate
    }
  `;

  /**
   * Emits every new Project after it was created.
   */
  public get projectAdded$(): Observable<Project> {
    return this.addProjectSubject.asObservable();
  }

  /**
   * Emits a Project whenever it has been updated.
   */
  public get projectUpdated$(): Observable<Project> {
    return this.updateProjectSubject.asObservable();
  }

  /**
   * Emits Projects whenever they have been deleted.
   */
  public get projectsDeleted$(): Observable<Project[]> {
    return this.deleteProjectsSubject.asObservable();
  }

  /**
   * Loads projects page based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch projects based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping, pagination.
   * It also fetches information about users who created and last updated the projects.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, pagination, and locale settings.
   * @return An Observable emitting an array of Project objects, each with additional user information.
   */
  public loadProjectsPage(selectParams: SelectParams): Observable<Project[]> {
    const query = gql`
      query Projects($organizationId: String!, $selectParams: SelectParams!) {
        projects(organizationId: $organizationId, selectParams: $selectParams) {
          ...ProjectInfo
        }
      }
      ${this.projectInfoGraphqlFragment}
    `;
    const variables = {
      organizationId: this.appManager.currentOrganization.id,
      selectParams: selectParams
    };

    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(Project, data['projects'] as Project[])));
  }

  /**
   * Loads all projects pages based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch projects based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping.
   * It also fetches information about users who created and last updated the projects.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, and locale settings.
   * @return An Observable emitting an array of Project objects, each with additional user information.
   */
  public loadAllProjects(selectParams: SelectParams): Observable<Project[]> {
    const pageSize = this.appConfig.MAX_REQUEST_PAGE_SIZE;
    const params = {...selectParams, start: 0, count: pageSize};
    return this.loadProjectsPage(params)
      .pipe(
        expand((projects, i) => {
          if (projects.length < pageSize) {
            return EMPTY;
          }
          params.start = (i + 1) * pageSize;
          return this.loadProjectsPage(params);
        }),
        reduce((all, page) => {
          all.push(...page);
          return all;
        }, [] as Project[])
      );
  }

  /**
   * Check that a Project entity does not have inaccessible assets for the current user.
   * @param projectId The ID of the project.
   * @return Observable that emits true if the Project is not related to at least one inaccessible asset, or false otherwise.
   */
  public projectHasNoInaccessibleAssets(projectId: string): Observable<boolean> {
    const query = gql`
      query ProjectDoesNotHaveInaccessibleAssets($projectId: String!) {
        projectHasNoInaccessibleAssets(id: $projectId)
      }
    `;
    const variables = {
      projectId
    };
    return this.apiService.query({query, variables})
      .pipe(map(data => data['projectHasNoInaccessibleAssets'] as boolean));
  }

  /**
   * Make an API request to create new Project with the provided data.
   * @param projectInput Project data.
   * @return Observable emitting the new Project that was created.
   */
  public createProject(projectInput: ProjectInput): Observable<Project> {
    const mutation = gql`
      mutation CreateProject($projectInput: CreateProjectInput!) {
        createProject(projectInput: $projectInput) {
          ...ProjectInfo
        }
      }
      ${this.projectInfoGraphqlFragment}
    `;
    const variables = {
      projectInput: {
        organizationId: this.appManager.currentOrganization.id,
        ...projectInput
      }
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Project, data['createProject'])),
        tap(newProject => this.addProjectSubject.next(newProject))
      );
  }

  /**
   * Call the API to update a Project.
   * @param project Project to update
   * @param projectInput Data to update the Project with.
   * @return Observable emitting the updated Project once the update is completed
   */
  public updateProject(project: Project, projectInput: ProjectInput): Observable<Project> {
    const mutation = gql`
      mutation UpdateProject($projectInput: UpdateProjectInput!) {
        updateProject(projectInput: $projectInput) {
          ...ProjectInfo
        }
      }
      ${this.projectInfoGraphqlFragment}
    `;
    const variables = {
      projectInput: {
        id: project.id,
        name: project.name,
        assetIds: project.assetIds,
        projectIds: project.projectIds,
        ...projectInput
      }
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Project, data['updateProject'])),
        tap(updatedProject => this.updateProjectSubject.next(updatedProject))
      );
  }

  /**
   * Call the API to update a list of Project.
   * @param projects List of objects containing project and projectInput to update.
   * @return Observable emitting updated Projects once the update is completed
   */
  public updateProjects(projects: { project: Project, projectInput: ProjectInput }[]): Observable<Project[]> {
    const mutation = gql`
      mutation UpdateProjects($projectInputs: [UpdateProjectInput!]!) {
        updateProjects(projectInputs: $projectInputs) {
          ...ProjectInfo
        }
      }
      ${this.projectInfoGraphqlFragment}
    `;
    const variables = {
      projectInputs: projects.map(({project: {id, name, assetIds, projectIds}, projectInput}) => {
        return {id, name, assetIds, projectIds, ...projectInput};
      })
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Project, data['updateProjects'] as Project[])),
        switchMap(projects => from(projects)),
        tap(updatedProject => this.updateProjectSubject.next(updatedProject)),
        toArray()
      );
  }

  /**
   * Make an API request to delete Projects.
   * @param projects Projects to delete.
   * @return True if all Projects have been deleted successfully, false otherwise.
   */
  public deleteProjects(projects: Project[]): Observable<boolean> {
    const mutation = gql`
      mutation DeleteProject($projectIds: [String!]!) {
        deleteProjects(ids: $projectIds)
      }
    `;
    const variables = {projectIds: projects.map(project => project.id)};

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => data['deleteProjects'] as boolean),
        tap(success => success && this.deleteProjectsSubject.next(projects))
      );
  }

  /**
   * Emits a Project whenever the side panel opens, or null whenever it closes.
   */
  public get sidePanelToggle$(): Observable<Project | null> {
    return this.sidePanelToggleSubject.asObservable();
  }

  /**
   * Open the sidenav with information about the selected Project.
   * @param project Project to be displayed in the side panel.
   */
  public openProjectSidePanel(project: Project): void {
    this.sidePanelToggleSubject.next(project);
  }

  /**
   * Close the side panel.
   */
  public closeProjectSidePanel(): void {
    this.sidePanelToggleSubject.next(null);
  }

  /**
   * Fetch a Project by its ID.
   * @param projectId The Project's ID.
   * @param fetchPolicy GraphQL query fetch policy, default is 'network-only'.
   * @return Observable emitting the Project.
   */
  public loadProject(projectId: string, fetchPolicy: FetchPolicy = 'network-only'): Observable<Project> {
    const query = gql`
      query Project($id: String!) {
        project(id: $id) {
          ...ProjectInfo
        }
      }
      ${this.projectInfoGraphqlFragment}
    `;
    const variables = {
      id: projectId
    };

    return this.apiService.query({query, variables, fetchPolicy})
      .pipe(
        map(data => plainToInstance(Project, data['project'])),
        tap(() => {
          if (fetchPolicy === 'cache-first') {
            this.activationEndService.getRefreshHeaderSubject().next();
          }
        })
      );
  }

  /**
   * Navigate to a Project's sheet.
   * @param projectId ID of the Project to navigate to.
   * @param tabName Name of the tab to open after navigating to the Project's sheet. Default tabName is features
   * @return Router's promise (return true if navigation succeeds)
   */
  public async navigateToProjectSheet(projectId: string, tabName = 'features'): Promise<void> {
    await this.router.navigate([
      'organization',
      this.appManager.currentOrganization.id,
      'projects',
      'project-sheet',
      projectId,
      tabName
    ]);
  }
}
